import React, { useEffect, useState, useCallback } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import lpApi from '../../helpers/lpApi';
import s from './LpPayment.css';

// =============================================================================
// LARGE PROJECTS — CLIENT CARD-ON-FILE capture (web parity with the app's
// small-jobs card-save). Saves a card as the client's DEFAULT payment method so
// the company's OFF-SESSION "Start milestone" charge (and the down-payment
// auto-capture cron) can charge it with the client absent.
//
// Reuses the web's existing app-wide Stripe.js loader (AsyncStripeProvider in
// App.js mounts <Elements> once with the decoded publishable key) — we just
// consume it with useStripe()/useElements(). No parallel Stripe loader.
//
// Mirrors the small-jobs card-save primitive: stripe.createPaymentMethod (card
// Elements) -> besthand-api saveClientCard (server SetupIntent confirm + attach
// + set as customer default). A 3D-Secure card returns requireAdditionalAction +
// setupClientSecret, finished here with stripe.confirmCardSetup, then re-saved.
// NEVER charges money. SEPARATE from the small-jobs checkout.
// =============================================================================

const Q_CARD = `
  query { myPaymentCardOnFile { status errorMessage savedCardBrand savedCardLast4 } }
`;
const M_SAVE = `
  mutation ($paymentMethod: String!, $cardLastFour: String!, $cardBrand: String) {
    saveClientCard(paymentMethod: $paymentMethod, cardLastFour: $cardLastFour, cardBrand: $cardBrand) {
      status errorMessage savedCardBrand savedCardLast4 requireAdditionalAction setupClientSecret
    }
  }
`;

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICONS = {
  card: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  lock: 'M6 10V8a6 6 0 1 1 12 0v2 M5 10h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  check: 'M20 6 9 17l-5-5',
};

const elementOptions = {
  style: {
    base: { color: '#0f1214', fontSize: '15px', fontFamily: 'inherit', '::placeholder': { color: '#9aa0a6' } },
    invalid: { color: '#b42318' },
  },
};

function LpCardOnFile(props) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null); // { brand, last4 }
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lpApi(Q_CARD);
      const env = res && res.data && res.data.myPaymentCardOnFile;
      if (env && env.status === 200 && env.savedCardLast4) {
        setSaved({ brand: env.savedCardBrand, last4: env.savedCardLast4 });
      } else {
        setSaved(null);
      }
    } catch (e) {
      setSaved(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCard(); }, [loadCard]);

  const persist = async (paymentMethodId, last4, brand) => {
    const res = await lpApi(M_SAVE, { paymentMethod: paymentMethodId, cardLastFour: last4, cardBrand: brand || null });
    const env = res && res.data && res.data.saveClientCard;
    if (res.errors || !env) {
      throw new Error((res.errors && res.errors[0] && res.errors[0].message) || 'Could not save the card.');
    }
    return env;
  };

  const save = async () => {
    if (busy) return;
    setError('');
    setNotice('');
    if (!stripe || !elements) {
      setError('Payment form is still loading — please try again.');
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setError('Please enter your card details.');
      return;
    }
    setBusy(true);
    try {
      // 1) Tokenize the card with Stripe (mirrors the small-jobs card-save).
      const pmRes = await stripe.createPaymentMethod({ type: 'card', card: cardNumber });
      if (pmRes.error || !pmRes.paymentMethod) {
        setBusy(false);
        setError(pmRes.error ? pmRes.error.message : 'Could not read the card.');
        return;
      }
      const pm = pmRes.paymentMethod;
      const last4 = pm.card && pm.card.last4;
      const brand = pm.card && pm.card.brand;

      // 2) Save server-side (SetupIntent confirm + attach + set as default).
      let env = await persist(pm.id, last4, brand);

      // 3) 3D-Secure follow-up if the card requires authentication.
      if (env.requireAdditionalAction && env.setupClientSecret) {
        const conf = await stripe.confirmCardSetup(env.setupClientSecret, { payment_method: pm.id });
        if (conf.error) {
          setBusy(false);
          setError(conf.error.message || 'Card authentication failed.');
          return;
        }
        // Re-call so the server attaches it + sets it as the default.
        env = await persist(pm.id, last4, brand);
      }

      if (env.status !== 200) {
        setBusy(false);
        setError(env.errorMessage || 'Could not save the card.');
        return;
      }

      setBusy(false);
      setEditing(false);
      setNotice('Card saved — it will be used for milestone charges.');
      setSaved({ brand: env.savedCardBrand || brand, last4: env.savedCardLast4 || last4 });
      if (props.onSaved) props.onSaved();
    } catch (e) {
      setBusy(false);
      setError(e.message || 'Could not save the card.');
    }
  };

  const showForm = editing || (!loading && !saved);

  return (
    <div className={s.wrap}>
      <div className={s.card}>
        <p className={s.cardTitle}><Icon d={ICONS.card} /> {props.title || 'Payment method for milestones'}</p>
        <p className={s.cardSub}>
          {props.subtitle
            || 'Save a card so each milestone is charged automatically as the work is approved. Your card is stored securely with Stripe.'}
        </p>

        {error ? <div className={s.alertErr}>{error}</div> : null}
        {notice ? <div className={s.alertOk}>{notice}</div> : null}

        {loading ? (
          <div className={s.savedNone}>Checking your saved card…</div>
        ) : saved && !showForm ? (
          <div className={s.savedRow}>
            <div className={s.savedLeft}>
              <span className={s.cardChip}><Icon d={ICONS.card} /></span>
              <div>
                <div className={s.savedBrand}>{saved.brand || 'Card'}</div>
                <div className={s.savedLast}>•••• {saved.last4}</div>
              </div>
            </div>
            <button type="button" className={s.btnGhost} onClick={() => { setEditing(true); setNotice(''); }}>
              Replace
            </button>
          </div>
        ) : null}

        {showForm ? (
          <div>
            <div className={s.fieldGroup}>
              <div>
                <span className={s.fieldLabel}>Card number</span>
                <div className={s.fieldBox}><CardNumberElement options={elementOptions} /></div>
              </div>
              <div className={s.fieldRow2}>
                <div>
                  <span className={s.fieldLabel}>Expiry</span>
                  <div className={s.fieldBox}><CardExpiryElement options={elementOptions} /></div>
                </div>
                <div>
                  <span className={s.fieldLabel}>CVC</span>
                  <div className={s.fieldBox}><CardCvcElement options={elementOptions} /></div>
                </div>
              </div>
            </div>
            <button type="button" className={s.btn} disabled={busy} onClick={save}>
              <Icon d={ICONS.lock} /> {busy ? 'Saving…' : (saved ? 'Update card' : 'Save card')}
            </button>
            {saved ? (
              <p style={{ textAlign: 'center', marginTop: 10 }}>
                <button type="button" className={s.btnGhost} disabled={busy} onClick={() => { setEditing(false); setError(''); }}>
                  Keep current card
                </button>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default withStyles(s)(LpCardOnFile);
